import { useEffect, useState } from "react";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
  /** URL slug for path-based routing (memso.ai/u/<handle>). Optional
   *  during onboarding; the SPA router redirects no-handle users to
   *  /onboarding to claim one. */
  handle?: string;
  /** ISO timestamp of the moment the user finished the onboarding
   *  wizard. Null = needs onboarding (auto-routes to /onboarding
   *  even if a handle was auto-derived from email pre-wizard, which
   *  is the case for users created during the multi-tenant refactor
   *  before 0010 migration shipped). */
  onboardedAt?: string | null;
}

export interface AuthState {
  loading: boolean;
  authenticated: boolean;
  user: AuthUser | null;
  csrfToken: string | null;
}

let cached: AuthState = { loading: true, authenticated: false, user: null, csrfToken: null };
const listeners = new Set<(s: AuthState) => void>();

function publish(state: AuthState) {
  cached = state;
  for (const l of listeners) l(state);
}

/** Fetch /auth/me. When the backend has no Google client configured the
 *  endpoint may not exist - treat any non-2xx as "auth disabled, you're
 *  through" so local LAN dev stays open. A hard 8s timeout makes sure
 *  the React tree never stays stuck on `Loading...`: if /auth/me hangs
 *  (slow tunnel, hung SW handler, partitioned network), the user
 *  lands on the Login canvas instead of an indefinite spinner. */
const AUTH_TIMEOUT_MS = 8000;

export async function refreshAuth(): Promise<AuthState> {
  try {
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = ctrl
      ? window.setTimeout(() => ctrl.abort(), AUTH_TIMEOUT_MS)
      : null;
    let res: Response;
    try {
      res = await fetch("/auth/me", {
        credentials: "same-origin",
        signal: ctrl?.signal,
      });
    } finally {
      if (timer !== null) window.clearTimeout(timer);
    }
    if (!res.ok) {
      const next: AuthState = {
        loading: false,
        authenticated: false,
        user: null,
        csrfToken: null,
      };
      publish(next);
      return next;
    }
    const body = (await res.json()) as { authenticated?: boolean; user?: AuthUser; csrfToken?: string };
    const next: AuthState = {
      loading: false,
      authenticated: !!body.authenticated,
      user: body.user || null,
      csrfToken: body.csrfToken || null,
    };
    publish(next);
    return next;
  } catch {
    const next: AuthState = { loading: false, authenticated: false, user: null, csrfToken: null };
    publish(next);
    return next;
  }
}

export function getCsrfToken(): string | null {
  return cached.csrfToken;
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>(cached);
  useEffect(() => {
    listeners.add(setState);
    if (cached.loading) refreshAuth();
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return state;
}
